// Local, privacy-friendly personalization.
//
// - Tracks which "topics" (series / tag groups) a user watches, stored ONLY in
//   the browser's localStorage. No server calls, no personal data leaves device.
// - The home page uses this to show a "Recommended for you" shelf based on the
//   user's most-viewed topics. New users (no history yet) get random videos.

const STORAGE_KEY = "rec_user_topics";

// Canonical known-series list (lowercase, normalized). Used to turn noisy tags
// or titles into a stable topic name so "pokémon", "Pokemon", "pokemon" all map
// to the same topic. Add more series here as the catalog grows.
const KNOWN_SERIES = [
  "naruto", "boruto", "pokemon", "pokemon asia", "shinchan", "shin chan",
  "ninja hattori", "oggy", "chhota bheem", "doraemon", "ben 10", "tom and jerry",
  "dragon ball", "one piece", "attack on titan", "demon slayer",
  "jujutsu kaisen", "haikyuu", "beyblade", "pikachu", "kick buttowski",
  "motu patlu", "little krishna", "digimon", "bakugan", "power rangers",
  "spider man", "spiderman", "superman", "batman", "mickey mouse",
  "scooby doo", "spongebob", "peppa pig", "paw patrol", "dora the explorer",
  "doraemon nobita", "cocomelon", "bluey", "krishna", "ramayana",
  "mahabharata", "tenali raman", "dino king", "dora", "saiki", "goku", "dbz",
];

// Generic tags that carry no topic signal (ignored when building topics).
const NOISE_TAGS = new Set([
  "episode", "episodes", "ep", "eps", "movie", "movies", "film", "tamil",
  "hindi", "telugu", "english", "dub", "dubbed", "sub", "subbed", "full",
  "full episode", "season", "seasons", "part", "parts", "part 1", "part 2",
  "series", "anime", "cartoon", "cartoons", "kids", "children", "new",
  "latest", "official", "video", "videos", "trailer", "teaser", "clip",
  "clips", "scene", "scenes", "song", "opening", "ending", "ost", "theme",
  "soundtrack", "promo", "adventure", "adventures", "channel", "upload",
  "today", "watch", "hd", "hd720", "1080p", "720p", "4k", "quality",
  "uploaded", "unknown", "na", "none", "n a", "longform", "short", "shorts",
  "live", "stream", "vlog", "poke", "pokemon asia", "compilation", "best of",
]);

// Strip accents + lowercase + collapse separators → "pokémon" → "pokemon"
const normalize = (s) => {
  if (!s || typeof s !== "string") return "";
  return s
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Find canonical known series present in a normalized haystack (word boundary).
const matchKnownSeries = (haystack) => {
  if (!haystack) return [];
  const found = [];
  KNOWN_SERIES.forEach((series) => {
    if (found.includes(series)) return;
    const re = new RegExp(`\\b${escapeRegex(series)}\\b`);
    if (re.test(haystack)) found.push(series);
  });
  return found;
};

// Return a stable list of "topics" for a video (canonical known series first,
// then meaningful non-noise tags). Used both to record history and to score
// recommendations, so topic strings MUST be consistent across videos.
export const getVideoTopics = (video) => {
  if (!video) return [];
  const topics = [];
  const push = (t) => {
    const n = normalize(t);
    if (n && n.length >= 3 && !topics.includes(n)) topics.push(n);
  };

  // tags (JSONB array) is the richest signal; title & author help fill gaps.
  const tags = (Array.isArray(video?.tags) ? video.tags : []).filter(
    (t) => typeof t === "string"
  );
  const title = video?.title || "";
  // NOTE: video.series is NOT used here — it defaults to "Naruto" for every row
  // in the DB, so it carries no real topic signal.
  const extra = [video?.customTag, video?.author?.title].filter(Boolean).join(" ");
  const haystack = normalize([...tags, title, extra].join(" "));

  // known series win (canonical & consistent across videos)
  matchKnownSeries(haystack).forEach((s) => push(s));

  // then keep meaningful non-noise tags for finer grouping (e.g. arcs)
  tags.forEach((t) => {
    const n = normalize(t);
    if (!n || n.length < 3) return;
    if (NOISE_TAGS.has(n)) return;
    // skip tags that are basically a known-series name (already added above)
    if (KNOWN_SERIES.some((s) => n.includes(s))) return;
    push(n);
  });

  return topics;
};

const RECORDED = new Set(); // per-session dedupe so a single open counts once

// Record that the user watched a video (increments its topic counts).
// Safe no-op if localStorage is unavailable.
export const recordVideoView = (video) => {
  try {
    const id = video?.videoId || video?.originalUrl || video?.url;
    if (!id || RECORDED.has(id)) return;
    RECORDED.add(id);

    const topics = getVideoTopics(video);
    if (!topics.length) return;

    let store = {};
    try {
      store = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch (e) {
      store = {};
    }
    if (!store || typeof store !== "object") store = {};

    const now = Date.now();
    topics.forEach((t) => {
      const cur = store[t] || { n: 0, t: now };
      store[t] = { n: (cur.n || 0) + 1, t: now };
    });

    // cap stored topics (keep the most recently viewed)
    const keys = Object.keys(store);
    if (keys.length > 50) {
      const sorted = keys.sort(
        (a, b) => (store[b]?.t || 0) - (store[a]?.t || 0)
      );
      const keep = new Set(sorted.slice(0, 50));
      Object.keys(store).forEach((k) => {
        if (!keep.has(k)) delete store[k];
      });
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    // ignore (storage may be blocked)
  }
};

// Returns `{ [topic]: { n: count, t: lastViewedTs } }` for the user's
// most-viewed topics, weighted by count and boosted by recency.
export const getUserTopTopics = (limit = 6) => {
  try {
    const store = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    if (!store || typeof store !== "object") return {};
    const entries = Object.entries(store).filter(
      ([, v]) => v && typeof v.n === "number" && v.n > 0
    );
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000; // 14 days
    entries.forEach(([, v]) => {
      const recent = v.t && v.t >= cutoff ? 1.5 : 1;
      v._w = v.n * recent;
    });
    entries.sort((a, b) => b[1]._w - a[1]._w || (b[1].t || 0) - (a[1].t || 0));
    const top = {};
    entries.slice(0, limit).forEach(([k, v]) => {
      top[k] = { n: v.n, t: v.t };
    });
    return top;
  } catch (e) {
    return {};
  }
};

// True once the user has watched at least one video (used to pick section title).
export const hasViewHistory = () => {
  try {
    const store = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    if (!store || typeof store !== "object") return false;
    return Object.values(store).some((v) => v && v.n > 0);
  } catch (e) {
    return false;
  }
};

export const getRecommendationTitle = () =>
  hasViewHistory() ? "Recommended for you" : "Picks for you";

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Core recommendation engine. Returns up to `count` videos from `allVideos`:
// - Returning user → videos matching their most-viewed topics (with a small
//   popularity boost + jitter), filled with random videos if not enough match.
// - New user (no history) → random videos.
export const getRecommendedVideos = (allVideos, count = 12) => {
  if (!Array.isArray(allVideos) || allVideos.length === 0) return [];
  const topTopics = getUserTopTopics(6);

  if (Object.keys(topTopics).length === 0) {
    return shuffle(allVideos).slice(0, count);
  }

  const scored = [];
  allVideos.forEach((v) => {
    const topics = getVideoTopics(v);
    if (!topics.length) return;
    let score = 0;
    topics.forEach((t) => {
      if (topTopics[t]) score += topTopics[t].n;
    });
    if (score > 0) {
      score += Math.log10((v?.stats?.views || 0) + 1) / 10; // popularity boost
      score += Math.random() * 0.6; // small jitter for variety
      scored.push({ v, score });
    }
  });

  scored.sort((a, b) => b.score - a.score);
  const picks = scored.map((s) => s.v);

  if (picks.length < count) {
    const pickedIds = new Set(picks.map((v) => v?.videoId));
    picks.push(...shuffle(allVideos.filter((v) => !pickedIds.has(v?.videoId))));
  }
  return picks.slice(0, count);
};
